import { useState } from 'react'
import { Alert, Button, Group, TextInput } from '@mantine/core'
import { PageTitle, SectionCard, StatTile } from '@lighthouse/design'
import { setApiKey, hasApiKey, hasAuthentication } from '../../services/api'
import { useApiHealth } from '../../hooks/useApiHealth'
import { useWorkerFleet } from '../../hooks/useOverview'
import { useSignalSummary } from '../../hooks/useSignals'
import { useDeadLetterStats } from '../../hooks/useQueues'
import { SLOW } from '../../utils/polling'
import { tileView } from '../signals/signalFormat'
import { activeJobsKpi } from './overviewFormat'
import { systemHealthKpi } from '../health/healthBoardFormat'

/**
 * API-key setup.
 *
 * Extracted out of the old dashboard, which gated the ENTIRE page behind it.
 * This was the only place the key could be set, so simply dropping the gate would
 * have stranded users on the API-key path with no way to configure it and every
 * other page broken. It is now a non-blocking banner: the Overview renders either
 * way, and the setup only appears when there is genuinely no credential.
 *
 * It no longer says the Overview is "showing sample data": nothing on it is a
 * sample any more. Without a credential every panel shows its own failed-request
 * state instead, which is the truth.
 */
function ApiKeySetup() {
  const [value, setValue] = useState('')
  const [saved, setSaved] = useState(false)

  if (saved || hasApiKey() || hasAuthentication()) return null

  return (
    <Alert color="blue" title="API access not configured" variant="light">
      <div style={{ fontSize: 12, marginBottom: 10 }}>
        Enter a Lighthouse API key to connect this browser to the API.
      </div>
      <Group gap="xs" align="flex-end">
        <TextInput
          size="xs"
          type="password"
          placeholder="Enter your API key"
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          style={{ minWidth: 240 }}
        />
        <Button
          size="xs"
          disabled={!value.trim()}
          onClick={() => {
            setApiKey(value.trim())
            setValue('')
            setSaved(true)
          }}
        >
          Save API key
        </Button>
      </Group>
    </Alert>
  )
}

/**
 * The page header band: title plus four KPIs, composed client-side from served
 * state — there is no KPI endpoint, and the fixture that fed this band is gone.
 *
 * - System Health: the API's own aggregate from `/api/health`, with how many
 *   checks stood behind it. No percentage; no formula exists for one. Never
 *   green over a gap the System Health panel shows: a group this instance did
 *   not check, or dead-letter queues not all inspected, drop the good tone
 *   (healthBoardFormat.systemHealthKpi).
 * - Active Jobs: busy worker slots from the worker projection — a dash when the
 *   fleet never reported or the projection is stale. Never a delta: no earlier
 *   sample is kept to compare against.
 * - Issues and Critical: open signals, and open CRITICAL signals, from the signal
 *   store (`api/observatory/signals/summary`) — each judged by the coverage that
 *   matches it (signalFormat.tileView). A zero is green only under Complete
 *   coverage; a detector that never ran, has stopped reporting, or a store not
 *   provisioned here is a dash with its reason, never a green 0 over a check
 *   that did not run. The counts include unconfirmed signals and say how many.
 *
 * Each tile carries its own loading and error sublabel, so a failed request and
 * a request in flight never look alike, and neither looks like a measurement.
 */
export function OverviewHeader() {
  const health = useApiHealth()
  const fleet = useWorkerFleet()
  const signals = useSignalSummary()
  // The rail badge's query at the badge's interval: shared cache, no added request.
  const deadLetters = useDeadLetterStats({ refetchInterval: SLOW })
  const healthTile = systemHealthKpi(health, deadLetters)
  const jobsTile = activeJobsKpi(fleet)
  const issuesTile = tileView(signals, 'all')
  const criticalTile = tileView(signals, 'critical')

  return (
    <>
      <ApiKeySetup />
      <PageTitle subtitle="Real-time overview of Lighthouse operations">
        The Observatory
      </PageTitle>

      <SectionCard>
        <div className="lh-overview-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <StatTile
            label="System Health"
            value={healthTile.value}
            status={healthTile.status}
            sublabel={healthTile.sublabel}
          />
          <StatTile label="Active Jobs" value={jobsTile.value} sublabel={jobsTile.sublabel} />
          <StatTile label="Issues" value={issuesTile.value} status={issuesTile.status} sublabel={issuesTile.sublabel} />
          <StatTile label="Critical" value={criticalTile.value} status={criticalTile.status} sublabel={criticalTile.sublabel} />
        </div>
      </SectionCard>
    </>
  )
}
